import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import rolesService from '../services/rolesService';
import staffService from '../services/staffService';
import { Plus, PencilSimple, Trash, Copy, UserPlus, ShieldCheck } from '@phosphor-icons/react';

const emptyForm = { name: '', permission_codes: [] };

function RolesPermissionsPage() {
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]); // [{id, code, module, description}]
  const [roles, setRoles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // null = create
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // role being assigned
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaffUserId, setSelectedStaffUserId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [cloneSource, setCloneSource] = useState(null); // role being cloned
  const [cloneName, setCloneName] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [catalogData, rolesData] = await Promise.all([
        rolesService.listPermissionCatalog(),
        rolesService.listRoles(),
      ]);
      setCatalog(Array.isArray(catalogData) ? catalogData : []);
      setRoles(Array.isArray(rolesData) ? rolesData : []);
    } catch (error) {
      toast.error('Failed to load roles & permissions');
    } finally {
      setLoading(false);
    }
  };

  const catalogByModule = useMemo(() => {
    const groups = {};
    for (const perm of catalog) {
      if (!groups[perm.module]) groups[perm.module] = [];
      groups[perm.module].push(perm);
    }
    return groups;
  }, [catalog]);

  const openCreateModal = () => {
    setEditingRole(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (role) => {
    setEditingRole(role);
    setFormData({ name: role.name, permission_codes: role.permission_codes });
    setShowModal(true);
  };

  const openCloneModal = (role) => {
    setCloneSource(role);
    setCloneName(`${role.name} (Copy)`);
  };

  const handleClone = async () => {
    if (!cloneName.trim()) {
      toast.error('Role name is required');
      return;
    }
    try {
      await rolesService.cloneRole(cloneSource.id, cloneName);
      toast.success('Role cloned');
      setCloneSource(null);
      setCloneName('');
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to clone role');
    }
  };

  const togglePermission = (code) => {
    setFormData((prev) => {
      const has = prev.permission_codes.includes(code);
      return {
        ...prev,
        permission_codes: has
          ? prev.permission_codes.filter((c) => c !== code)
          : [...prev.permission_codes, code],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Role name is required');
      return;
    }
    setSubmitting(true);
    try {
      if (editingRole) {
        await rolesService.updateRole(editingRole.id, formData);
        toast.success('Role updated');
      } else {
        await rolesService.createRole(formData);
        toast.success('Role created');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save role');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await rolesService.deleteRole(deleteTarget);
      toast.success('Role deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to delete role');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openAssignModal = async (role) => {
    setAssignTarget(role);
    setSelectedStaffUserId('');
    try {
      const data = await staffService.listStaff();
      const list = Array.isArray(data) ? data : data?.items || [];
      setStaffOptions(list.filter((s) => s.user_id));
    } catch (error) {
      toast.error('Failed to load staff list');
    }
  };

  const handleAssign = async () => {
    if (!selectedStaffUserId) {
      toast.error('Select a staff member first');
      return;
    }
    setAssigning(true);
    try {
      await rolesService.assignRoleToUser(selectedStaffUserId, assignTarget.id);
      toast.success('Role assigned');
      setAssignTarget(null);
    } catch (error) {
      toast.error(error.message || 'Failed to assign role');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading roles & permissions...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Roles & Permissions</h1>
            <p>Compose custom staff roles from the permission catalog. System roles are read-only — clone one to start from its defaults.</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            New Role
          </button>
        </div>

        <div className="table-container">
          {roles.length === 0 ? (
            <div className="empty-state">
              <p>No roles yet.</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Permissions</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.id}>
                    <td>{role.name}</td>
                    <td>{role.is_system ? 'System' : 'Custom'}</td>
                    <td>{role.permission_codes.length} granted</td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Clone into a new custom role" onClick={() => openCloneModal(role)}>
                        <Copy size={18} />
                      </button>
                      {!role.is_system && (
                        <>
                          <button className="btn-icon" title="Assign to a staff member" onClick={() => openAssignModal(role)}>
                            <UserPlus size={18} />
                          </button>
                          <button className="btn-icon" title="Edit" onClick={() => openEditModal(role)}>
                            <PencilSimple size={18} />
                          </button>
                          <button className="btn-icon btn-danger" title="Delete" onClick={() => setDeleteTarget(role.id)}>
                            <Trash size={18} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 720 }}>
            <div className="modal-header">
              <h2>{editingRole ? 'Edit Role' : 'New Role'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Role Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Permissions</label>
                {Object.keys(catalogByModule).length === 0 ? (
                  <p style={{ color: 'var(--text-secondary, #666)' }}>
                    No permissions have been seeded yet — run scripts/seed_permissions.py.
                  </p>
                ) : (
                  Object.entries(catalogByModule).map(([module, perms]) => (
                    <div key={module} style={{ marginBottom: 16 }}>
                      <strong style={{ textTransform: 'capitalize' }}>{module}</strong>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 6 }}>
                        {perms.map((perm) => (
                          <label key={perm.code} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontWeight: 'normal' }}>
                            <input
                              type="checkbox"
                              checked={formData.permission_codes.includes(perm.code)}
                              onChange={() => togglePermission(perm.code)}
                              style={{ width: 'auto', marginTop: 3 }}
                            />
                            <span title={perm.code}>{perm.description}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingRole ? 'Update' : 'Create'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {assignTarget && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2><ShieldCheck size={20} style={{ verticalAlign: 'middle', marginRight: 6 }} />Assign "{assignTarget.name}"</h2>
              <button type="button" className="btn-close" onClick={() => setAssignTarget(null)} aria-label="Close">×</button>
            </div>
            <div className="form-group">
              <label>Staff Member</label>
              <select value={selectedStaffUserId} onChange={(e) => setSelectedStaffUserId(e.target.value)}>
                <option value="">Select staff...</option>
                {staffOptions.map((staff) => (
                  <option key={staff.user_id} value={staff.user_id}>
                    {staff.first_name} {staff.last_name} — {staff.position}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" disabled={assigning} onClick={handleAssign}>
                {assigning ? 'Assigning...' : 'Assign'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setAssignTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {cloneSource && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: 380, padding: '16px' }}>
            <div className="modal-header" style={{ marginBottom: '12px' }}>
              <h2 style={{ margin: '0' }}>Clone Role</h2>
              <button type="button" className="btn-close" onClick={() => setCloneSource(null)} aria-label="Close">×</button>
            </div>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label style={{ marginBottom: '6px' }}>Name *</label>
              <input
                type="text"
                value={cloneName}
                onChange={(e) => setCloneName(e.target.value)}
                placeholder={`${cloneSource.name} (Copy)`}
                autoFocus
              />
            </div>
            <div className="form-actions" style={{ gap: '8px' }}>
              <button type="button" className="btn btn-primary" onClick={handleClone}>
                Clone
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setCloneSource(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Role"
        message="This will permanently delete this custom role. Any staff still assigned to it must be reassigned first."
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default RolesPermissionsPage;
